import { Stack, styled } from '@mui/material';
import { matchPath, useLocation } from 'react-router-dom';

import { useNewRelicIdentifyEvent } from '~/src/hooks/useNewRelicIdentifyEvent';
import { usePortalAppcues } from '~/src/hooks/usePortalAppcues';
import { usePortalFullStory } from '~/src/hooks/usePortalFullStory';
import { salesSimulationFullPage } from '~/src/views/Simulate/pages/Simulate/constants/links';

import { FullScreenBannerWrapper } from './components/FullScreenBannerWrapper';
import { MobileMenuHeader } from './components/MobileMenuHeader';
import { NavigationBar } from './components/NavigationBar/NavigationBar';
import { NavigationMenu } from './components/NavigationMenu';

const CONTENT_DESKTOP_MAX_WIDTH = '1000px';
const NAVIGATION_MENU_DESKTOP_WIDTH = '320px';
const NAVIGATION_MENU_MOBILE_WIDTH = '270px';

export const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 0 0',
  gap: theme.spacing(4),
  justifyContent: 'center',
  overflow: 'hidden',

  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const Aside = styled('aside')(({ theme }) => ({
  borderRight: `1px solid ${theme.borderColors.default}`,
  display: 'flex',
  flex: `0 0 ${NAVIGATION_MENU_DESKTOP_WIDTH}`,

  [theme.breakpoints.down('lg')]: {
    flex: `0 0 ${NAVIGATION_MENU_MOBILE_WIDTH}`,
  },

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const Content = styled(Stack)(() => ({
  flex: '1 0 0',
  overflowY: 'auto',
}));

type MainProps = {
  pathname?: string;
};

export const Main = styled('main')<MainProps>(({ pathname, theme }) => ({
  '&::after': {
    content: '""',
    minHeight: theme.spacing(4),

    [theme.breakpoints.down('lg')]: {
      minHeight: theme.spacing(1),
    },
  },
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  margin: theme.spacing(4, 4, 0, 0),
  maxWidth: CONTENT_DESKTOP_MAX_WIDTH,
  padding: theme.spacing(3, 0, 0, 3),

  [theme.breakpoints.down('lg')]: {
    margin: theme.spacing(1, 1, 0, 0),
    padding: theme.spacing(1, 0, 0, 1),
  },

  [theme.breakpoints.down('xl')]: {
    maxWidth: '100%',
    width: 'auto',
  },

  width: '100%',

  ...(pathname &&
    matchPath({ path: '/intelligence/*' }, pathname) && {
      '& iframe': {
        border: 0,
        height: '100%',
        marginLeft: '-12px', // -12px comes from sigma's internal padding value
        width: 'calc(100% + 12px + 12px)', // to make up for the 12px margin left and right
      },
      '&::after': {
        content: '""',
        minHeight: theme.spacing(0),

        [theme.breakpoints.down('lg')]: {
          minHeight: theme.spacing(0),
        },
      },

      margin: 0,
      maxWidth: 'unset',
      padding: theme.spacing(7, 4, 7, 3),

      [theme.breakpoints.down('lg')]: {
        padding: theme.spacing(1),
      },
    }),
}));

export const NavigationMenuContainer = styled('div')(({ theme }) => ({
  '&::after': {
    content: '""',
    minHeight: theme.spacing(4),

    [theme.breakpoints.down('lg')]: {
      minHeight: theme.spacing(1),
    },
  },
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(4),
  overflowY: 'auto',
  padding: theme.spacing(4, 0, 0, 4),

  [theme.breakpoints.down('lg')]: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2, 0, 0, 1),
  },

  width: '100%',
}));

export const Layout: React.FC = ({ children }) => {
  const location = useLocation();
  usePortalFullStory();
  useNewRelicIdentifyEvent();
  usePortalAppcues();

  if (
    location.pathname.includes('/account/invoices/details/') ||
    location.pathname.includes('/resend_verification') ||
    location.pathname.includes(salesSimulationFullPage)
  ) {
    return <>{children}</>;
  }

  return (
    <>
      <FullScreenBannerWrapper />
      <NavigationBar />

      <Container>
        <Aside>
          <NavigationMenu />
        </Aside>

        <Content>
          <MobileMenuHeader />
          <Main pathname={location.pathname}>{children}</Main>
        </Content>
      </Container>
    </>
  );
};
